import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import fallbackImage from "../assets/images/service.jpg";
import SearchInput from "../components/SearchInput";
import { useAuth } from "../context/AuthContext";
import { useGetServiceQuery, useLazyGetSlotsQuery, useCreateBookingMutation, useCreateCustomBookingMutation } from "../store/api/servicesApi";

// Each booking reserves one car; this only caps the informational party size.
const MAX_GUESTS = 8;

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function toKey(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function parseDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function Calendar({ selectedDate, onSelect, startDate, endDate }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const initial = startDate && parseDate(startDate) > today ? parseDate(startDate) : today;
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const isToday = (day) =>
    day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();

  const isDisabled = (day) => {
    const d = new Date(viewYear, viewMonth, day);
    d.setHours(0, 0, 0, 0);
    if (d < today) return true;
    if (startDate && d < parseDate(startDate)) return true;
    if (endDate && d > parseDate(endDate)) return true;
    return false;
  };

  const isSelected = (day) => selectedDate === toKey(viewYear, viewMonth, day);

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} className="p-1 rounded hover:bg-gray-100 transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-secondary">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <span className="font-manrope-semibold text-sm text-black">{MONTHS[viewMonth]} {viewYear}</span>
        <button onClick={nextMonth} className="p-1 rounded hover:bg-gray-100 transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-secondary">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-7 mb-2">
        {DAYS.map((d) => (
          <div key={d} className="text-center font-manrope-medium text-xs text-secondary py-1">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-y-1">
        {cells.map((day, i) => {
          if (!day) return <div key={`e-${i}`} />;
          const disabled = isDisabled(day);
          const selected = isSelected(day);
          return (
            <button
              key={day}
              disabled={disabled}
              onClick={() => onSelect(toKey(viewYear, viewMonth, day))}
              className={`
                mx-auto w-8 h-8 sm:w-9 sm:h-9 rounded-lg text-xs sm:text-sm font-manrope-medium transition-colors flex items-center justify-center
                ${selected ? "bg-primary text-white font-manrope-bold" : ""}
                ${!selected && isToday(day) ? "border border-primary text-primary" : ""}
                ${!selected && !isToday(day) && !disabled ? "text-black hover:bg-primary/10" : ""}
                ${disabled ? "text-gray-300 cursor-not-allowed" : "cursor-pointer"}
              `}
            >
              {String(day).padStart(2, "0")}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Custom Booking Modal ── */
function CustomBookingModal({ service, user, onClose }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    date: "",
    startTime: "",
    endTime: "",
    description: "",
    notes: "",
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    address: "",
  });
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const [createCustomBooking, { isLoading }] = useCreateCustomBookingMutation();

  const setField = (key) => (e) => { setForm((f) => ({ ...f, [key]: e.target.value })); setError(""); };
  const today = new Date().toISOString().split("T")[0];

  const validate = () => {
    if (step === 0) {
      if (!form.date) return "Please select a date.";
      if (!form.startTime) return "Please enter a start time.";
      if (!form.endTime) return "Please enter an end time.";
      if (form.startTime >= form.endTime) return "End time must be after start time.";
      if (!form.description.trim()) return "Please describe what you need.";
      if (form.description.trim().length < 10) return "Description must be at least 10 characters.";
    }
    if (step === 1) {
      if (!form.name.trim()) return "Full name is required.";
      if (!form.email.trim()) return "Email is required.";
      if (!form.phone.trim()) return "Phone number is required.";
      if (!form.address.trim()) return "Address is required.";
    }
    return "";
  };

  const next = () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError("");
    setStep((s) => s + 1);
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError("");
    try {
      const res = await createCustomBooking({
        serviceId: service.id,
        preferredDate: form.date,
        preferredTimeSlot: `${form.startTime} – ${form.endTime}`,
        serviceDescription: form.description,
        customerName: form.name,
        customerEmail: form.email,
        customerPhone: form.phone,
        address: form.address,
        notes: form.notes,
      }).unwrap();
      setDone(res.data);
    } catch (err) {
      setError(err?.data?.error?.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
          <div>
            <h2 className="font-manrope-bold text-lg text-black">Custom Booking Request</h2>
            <p className="font-manrope-regular text-xs text-secondary mt-0.5">{service.title}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-secondary">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {done ? (
          <div className="p-8 flex flex-col items-center gap-5 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8 text-green-500">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            </div>
            <div>
              <h3 className="font-manrope-bold text-xl text-black">Request submitted!</h3>
              <p className="font-manrope-regular text-sm text-secondary mt-1 leading-relaxed">
                We'll review your request and send you a price offer. You can track it in your account under "Custom requests".
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl px-5 py-3 w-full text-left">
              <p className="font-manrope-medium text-xs text-secondary uppercase tracking-wide">Booking reference</p>
              <p className="font-manrope-bold text-base text-primary mt-0.5">{done.bookingNumber}</p>
            </div>
            <div className="flex gap-3 w-full">
              <button
                onClick={onClose}
                className="flex-1 py-3 border border-gray-200 text-secondary font-manrope-semibold text-sm rounded-xl hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <a
                href="/account"
                className="flex-1 py-3 bg-primary text-white font-manrope-semibold text-sm rounded-xl hover:opacity-90 transition-opacity text-center"
              >
                View in account
              </a>
            </div>
          </div>
        ) : (
          <div className="p-6 flex flex-col gap-5">
            {/* Progress bar */}
            <div className="flex gap-2">
              {["Date & time", "Your details"].map((label, i) => (
                <div key={i} className="flex-1 flex flex-col gap-1.5">
                  <div className={`h-1.5 rounded-full transition-colors ${i <= step ? "bg-primary" : "bg-gray-100"}`} />
                  <span className={`font-manrope-medium text-xs ${i === step ? "text-primary" : "text-gray-400"}`}>{label}</span>
                </div>
              ))}
            </div>

            {/* Step 0: Date, time & description */}
            {step === 0 && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="font-manrope-medium text-sm text-secondary">Preferred date <span className="text-red-400">*</span></label>
                  <input
                    type="date"
                    min={today}
                    value={form.date}
                    onChange={setField("date")}
                    className="border border-gray-200 rounded-xl px-4 py-2.5 font-manrope-regular text-sm text-black outline-none focus:border-primary transition-colors"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <label className="font-manrope-medium text-sm text-secondary">Start time <span className="text-red-400">*</span></label>
                    <input
                      type="time"
                      value={form.startTime}
                      onChange={setField("startTime")}
                      className="border border-gray-200 rounded-xl px-4 py-2.5 font-manrope-regular text-sm text-black outline-none focus:border-primary transition-colors"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="font-manrope-medium text-sm text-secondary">End time <span className="text-red-400">*</span></label>
                    <input
                      type="time"
                      value={form.endTime}
                      onChange={setField("endTime")}
                      className="border border-gray-200 rounded-xl px-4 py-2.5 font-manrope-regular text-sm text-black outline-none focus:border-primary transition-colors"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="font-manrope-medium text-sm text-secondary">What do you need? <span className="text-red-400">*</span></label>
                  <textarea
                    rows={4}
                    value={form.description}
                    onChange={setField("description")}
                    placeholder="Describe the service you need, number of people, any specific requirements…"
                    className="border border-gray-200 rounded-xl px-4 py-3 font-manrope-regular text-sm text-black outline-none focus:border-primary transition-colors resize-none leading-relaxed"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="font-manrope-medium text-sm text-secondary">Additional notes <span className="text-gray-400">(optional)</span></label>
                  <textarea
                    rows={2}
                    value={form.notes}
                    onChange={setField("notes")}
                    placeholder="Any other information…"
                    className="border border-gray-200 rounded-xl px-4 py-3 font-manrope-regular text-sm text-black outline-none focus:border-primary transition-colors resize-none"
                  />
                </div>
              </div>
            )}

            {/* Step 1: Contact info */}
            {step === 1 && (
              <div className="flex flex-col gap-4">
                <div className="bg-gray-50 rounded-xl px-4 py-3">
                  <p className="font-manrope-medium text-xs text-secondary uppercase tracking-wide mb-1">Booking summary</p>
                  <p className="font-manrope-semibold text-sm text-black">
                    {new Date(form.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                    &nbsp;·&nbsp;{form.startTime} – {form.endTime}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { key: "name", label: "Full name", type: "text", placeholder: "Your full name" },
                    { key: "email", label: "Email", type: "email", placeholder: "your@email.com" },
                    { key: "phone", label: "Phone / WhatsApp", type: "tel", placeholder: "+1 000 000 0000" },
                    { key: "address", label: "Address", type: "text", placeholder: "Pickup / service address" },
                  ].map(({ key, label, type, placeholder }) => (
                    <div key={key} className="flex flex-col gap-1.5">
                      <label className="font-manrope-medium text-sm text-secondary">{label} <span className="text-red-400">*</span></label>
                      <input
                        type={type}
                        value={form[key]}
                        onChange={setField(key)}
                        placeholder={placeholder}
                        className="border border-gray-200 rounded-xl px-4 py-2.5 font-manrope-regular text-sm text-black outline-none focus:border-primary transition-colors"
                      />
                    </div>
                  ))}
                </div>

                <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                  <p className="font-manrope-semibold text-sm text-amber-700 mb-1">How it works</p>
                  <ul className="flex flex-col gap-1">
                    {[
                      "We'll review your request and send a price offer",
                      "You can accept the price or negotiate from your account",
                      "Once agreed, we'll confirm your booking",
                    ].map((text, i) => (
                      <li key={i} className="font-manrope-regular text-xs text-amber-700 flex items-start gap-2">
                        <span className="flex-shrink-0 mt-0.5">{i + 1}.</span>
                        {text}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                <p className="font-manrope-medium text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Navigation */}
            <div className="flex gap-3">
              {step > 0 && (
                <button
                  onClick={() => { setStep((s) => s - 1); setError(""); }}
                  className="flex-1 py-3 border border-gray-200 text-secondary font-manrope-semibold text-sm rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
              )}
              {step === 0 ? (
                <button
                  onClick={next}
                  className="flex-1 py-3 bg-primary text-white font-manrope-semibold text-sm rounded-xl hover:opacity-90 transition-opacity"
                >
                  Continue
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="flex-1 py-3 bg-primary text-white font-manrope-semibold text-sm rounded-xl hover:opacity-90 disabled:opacity-60 transition-opacity flex items-center justify-center gap-2"
                >
                  {isLoading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {isLoading ? "Submitting…" : "Send request"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Success / pending state ── */
function PendingView({ booking, service, slot, onBookAnother }) {
  return (
    <div className="bg-bg min-h-screen py-16">
      <div className="container mx-auto px-4 max-w-lg">
        <div className="bg-white rounded-2xl p-8 shadow-sm flex flex-col items-center gap-6 text-center">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8 text-amber-500">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h2 className="font-manrope-bold text-2xl text-black">Booking requested!</h2>
            <p className="font-manrope-regular text-sm text-secondary mt-2 leading-relaxed">
              Your booking request is pending approval. Once approved by our team, you'll receive your app login credentials by email.
            </p>
          </div>

          <div className="w-full bg-gray-50 rounded-xl p-4 text-left flex flex-col gap-2">
            <p className="font-manrope-medium text-xs text-secondary uppercase tracking-wide">Booking summary</p>
            <div className="flex justify-between text-sm">
              <span className="text-secondary font-manrope-regular">Booking #</span>
              <span className="font-manrope-bold text-black">{booking.bookingNumber}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-secondary font-manrope-regular">Service</span>
              <span className="font-manrope-semibold text-black">{service?.title}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-secondary font-manrope-regular">Date & time</span>
              <span className="font-manrope-semibold text-black">
                {slot?.date} · {slot?.startTime}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-secondary font-manrope-regular">Status</span>
              <span className="inline-flex items-center gap-1.5 font-manrope-semibold text-amber-500">
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                Pending approval
              </span>
            </div>
          </div>

          <div className="w-full bg-teal-50 border border-teal-200 rounded-xl p-4 text-left">
            <p className="font-manrope-semibold text-sm text-teal-700">What happens next?</p>
            <ul className="mt-2 flex flex-col gap-1.5">
              <li className="font-manrope-regular text-xs text-teal-600 flex items-start gap-2">
                <span className="mt-0.5 flex-shrink-0">✓</span>
                Our team reviews your booking request
              </li>
              <li className="font-manrope-regular text-xs text-teal-600 flex items-start gap-2">
                <span className="mt-0.5 flex-shrink-0">✓</span>
                Once approved, you'll receive an email with your app login credentials
              </li>
              <li className="font-manrope-regular text-xs text-teal-600 flex items-start gap-2">
                <span className="mt-0.5 flex-shrink-0">✓</span>
                Use the credentials to log in to the Contentour Bonaire app
              </li>
            </ul>
          </div>

          <div className="flex gap-3 w-full">
            <Link
              to="/account"
              className="flex-1 border border-border text-black font-manrope-semibold text-sm py-3 rounded-xl hover:bg-gray-50 transition-colors text-center"
            >
              My bookings
            </Link>
            <button
              onClick={onBookAnother}
              className="flex-1 bg-primary text-white font-manrope-semibold text-sm py-3 rounded-xl hover:opacity-90 transition-opacity"
            >
              Book another
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ServiceBooking() {
  const { id } = useParams();
  const { user, openModal } = useAuth();

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [bookingResult, setBookingResult] = useState(null);
  const [error, setError] = useState("");
  const [customTimeEnabled, setCustomTimeEnabled] = useState(false);
  const [customTime, setCustomTime] = useState("");
  const [showCustomModal, setShowCustomModal] = useState(false);

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    address: "",
    notes: "",
  });

  const { data: serviceData, isLoading: serviceLoading, isError: serviceError } = useGetServiceQuery(id);
  const service = serviceData?.data || null;

  const [fetchSlots, { data: slotsData, isLoading: slotsLoading }] = useLazyGetSlotsQuery();
  const [createBooking, { isLoading: isSubmitting }] = useCreateBookingMutation();

  const slots = slotsData?.data || [];

  // Pre-fill form from user when they're logged in
  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setCustomTimeEnabled(false);
    setCustomTime("");
    if (service) fetchSlots({ serviceId: service.id, date });
  };

  const handleCustomTime = () => {
    if (!customTime) return;
    setSelectedSlot({ id: "custom", startTime: customTime, endTime: "", availableSpots: 10, bookedCount: 0, status: "available" });
    setCustomTimeEnabled(false);
  };

  const maxQty = MAX_GUESTS;
  const servicePrice = service?.price || 0;
  const subtotal = servicePrice;
  const image = service?.images?.[0] || fallbackImage;

  const handleRequestBooking = async () => {
    setError("");
    if (!user) { openModal("sign-in"); return; }
    if (!selectedDate || !selectedSlot) { setError("Please select a date and time slot."); return; }
    const name = form.fullName || user.name || "";
    const email = form.email || user.email || "";
    const phone = form.phone || user.phone || "";
    const address = form.address;
    if (!name || !email || !phone || !address) {
      setError("Please fill in all required fields.");
      return;
    }
    try {
      const res = await createBooking({
        serviceId: service.id,
        date: selectedDate,
        timeSlot: selectedSlot.startTime,
        quantity,
        customerName: name,
        customerEmail: email,
        customerPhone: phone,
        address,
        notes: form.notes,
      }).unwrap();
      setBookingResult(res.data);
    } catch (err) {
      setError(err?.data?.error?.message || "Something went wrong. Please try again.");
    }
  };

  const handleBookAnother = () => {
    setBookingResult(null);
    setSelectedDate(null);
    setSelectedSlot(null);
    setQuantity(1);
    setError("");
    setCustomTimeEnabled(false);
    setCustomTime("");
    setForm({ fullName: "", email: "", phone: "", address: "", notes: "" });
  };

  if (serviceLoading) {
    return (
      <div className="bg-bg min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="font-manrope-regular text-sm text-secondary">Loading service…</p>
        </div>
      </div>
    );
  }

  if (serviceError || !service) {
    return (
      <div className="bg-bg min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="font-manrope-medium text-base text-red-500">Service not found.</p>
          <Link to="/services" className="font-manrope-semibold text-sm text-primary hover:opacity-80 mt-2 block">
            Back to services
          </Link>
        </div>
      </div>
    );
  }

  if (bookingResult) {
    return (
      <PendingView
        booking={bookingResult}
        service={service}
        slot={selectedSlot}
        onBookAnother={handleBookAnother}
      />
    );
  }

  return (
    <div>
      {/* Custom Booking Modal */}
      {showCustomModal && (
        <CustomBookingModal
          service={service}
          user={user}
          onClose={() => setShowCustomModal(false)}
        />
      )}

      {/* Hero banner */}
      <div className="relative bg-[#1a7b7b]">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-10 -right-10 w-52 md:w-72 opacity-80">
            <div style={{
              background: "linear-gradient(135deg, #a8edea 0%, #fed6e3 40%, #c3f0e8 70%, #b8d4f8 100%)",
              borderRadius: "30% 70% 70% 30% / 30% 30% 70% 70%",
              height: "220px", filter: "blur(2px)", opacity: 0.7,
            }} />
          </div>
          <div className="absolute -bottom-10 -left-10 w-40 md:w-56 opacity-60">
            <div style={{
              background: "linear-gradient(135deg, #c3f0e8 0%, #a8edea 50%, #fed6e3 100%)",
              borderRadius: "70% 30% 30% 70% / 70% 70% 30% 30%",
              height: "180px", filter: "blur(2px)", opacity: 0.6,
            }} />
          </div>
        </div>
        <div className="container mx-auto px-4 py-14 md:py-20 flex flex-col items-center gap-4 relative z-10">
          <div className="flex flex-wrap items-center justify-center gap-1 text-white/70 font-manrope-regular text-sm">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/services" className="hover:text-white transition-colors">All services</Link>
            <span>/</span>
            <span className="text-white/60">{service.title}</span>
            <span>/</span>
            <span className="text-white">Booking</span>
          </div>
          <h1 className="font-manrope-bold text-[32px] md:text-[48px] text-white text-center">Book service</h1>
          <div className="flex justify-center w-full max-w-lg">
            <SearchInput />
          </div>
        </div>
      </div>

      {/* Booking area */}
      <div className="bg-bg py-12 md:py-16">
        <div className="container mx-auto px-4 flex flex-col lg:flex-row gap-6 items-start">

          {/* LEFT — Calendar + slots */}
          <div className="w-full min-w-0 lg:flex-1 bg-white rounded-2xl p-4 sm:p-6 shadow-sm">
            {/* Service images */}
            {service.images && service.images.length > 0 && (
              <div className="mb-6 -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 rounded-t-2xl overflow-hidden">
                {service.images.length === 1 ? (
                  <img src={service.images[0]} alt={service.title} className="w-full h-56 object-cover" />
                ) : (
                  <div className="grid grid-cols-2 gap-1 h-56">
                    <img src={service.images[0]} alt={service.title} className="w-full h-full object-cover" />
                    <div className={`grid gap-1 ${service.images.length >= 4 ? "grid-rows-2" : "grid-rows-1"}`}>
                      {service.images.slice(1, service.images.length >= 4 ? 3 : 2).map((img, i) => (
                        <div key={i} className="relative overflow-hidden">
                          <img src={img} alt={`${service.title} ${i + 2}`} className="w-full h-full object-cover" />
                          {i === 1 && service.images.length > 4 && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <span className="font-manrope-bold text-white text-lg">+{service.images.length - 3}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Service title & description */}
            <div className="mb-6 pb-6 border-b border-gray-100">
              <h2 className="font-manrope-bold text-xl text-black">{service.title}</h2>
              {service.description && (
                <p className="font-manrope-regular text-sm text-secondary mt-2 leading-relaxed">{service.description}</p>
              )}
            </div>

            <h2 className="font-manrope-bold text-lg text-black mb-6">Select date</h2>
            <Calendar
              selectedDate={selectedDate}
              onSelect={handleDateSelect}
              startDate={service.startDate}
              endDate={service.endDate}
            />

            {/* Legend */}
            <div className="flex flex-wrap gap-4 mt-6 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-primary" />
                <span className="font-manrope-regular text-xs text-secondary">Selected</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded border border-primary" />
                <span className="font-manrope-regular text-xs text-secondary">Today</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gray-200" />
                <span className="font-manrope-regular text-xs text-secondary">Unavailable</span>
              </div>
            </div>

            {/* Time slots */}
            {selectedDate && (
              <div className="mt-6 pt-5 border-t border-gray-100 flex flex-col gap-4">
                <h3 className="font-manrope-bold text-base text-black">Select time slot</h3>

                {slotsLoading && (
                  <div className="flex items-center gap-2 py-4">
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span className="font-manrope-regular text-sm text-secondary">Loading slots…</span>
                  </div>
                )}

                {!slotsLoading && slots.length === 0 && (
                  <p className="font-manrope-regular text-sm text-secondary py-2">
                    No time slots available for this date.
                  </p>
                )}

                {!slotsLoading && slots.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {slots.map((slot) => {
                      const fullyBooked = slot.status === "fully_booked" || slot.status === "closed";
                      const remaining = slot.availableSpots - slot.bookedCount;
                      const isSelected = selectedSlot?.id === slot.id;
                      return (
                        <button
                          key={slot.id}
                          disabled={fullyBooked}
                          onClick={() => { setSelectedSlot(slot); setQuantity(1); setCustomTimeEnabled(false); }}
                          className={`flex flex-col items-start px-3 py-2 rounded-xl border text-sm font-manrope-medium transition-colors
                            ${isSelected
                              ? "bg-primary border-primary text-white"
                              : fullyBooked
                              ? "border-gray-200 text-gray-300 cursor-not-allowed bg-gray-50"
                              : "border-gray-200 text-secondary hover:border-primary hover:text-primary bg-white"
                            }`}
                        >
                          <span className="flex items-center gap-1.5">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 flex-shrink-0">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {slot.startTime} – {slot.endTime}
                          </span>
                          <span className={`text-xs mt-0.5 ${isSelected ? "text-white/70" : fullyBooked ? "text-gray-300" : "text-gray-400"}`}>
                            {fullyBooked ? "Fully booked" : `${remaining} car${remaining !== 1 ? "s" : ""} left today`}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Custom time option */}
                {!slotsLoading && (
                  <div className="mt-3">
                    {!customTimeEnabled ? (
                      <button
                        onClick={() => { setCustomTimeEnabled(true); setSelectedSlot(null); }}
                        className="text-xs font-manrope-medium text-primary hover:underline flex items-center gap-1"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                        {slots.length > 0 ? "Prefer a different time? Enter a custom time" : "Enter a custom time"}
                      </button>
                    ) : (
                      <div className="border border-primary/30 rounded-xl p-4 bg-primary/5 flex flex-col gap-3">
                        <p className="font-manrope-semibold text-sm text-black">Custom time</p>
                        <div className="flex flex-wrap items-center gap-3">
                          <input
                            type="time"
                            value={customTime}
                            onChange={(e) => setCustomTime(e.target.value)}
                            className="border border-gray-200 rounded-xl px-3 py-2 font-manrope-regular text-sm text-gray-700 outline-none focus:border-primary transition-colors"
                          />
                          <button
                            onClick={handleCustomTime}
                            disabled={!customTime}
                            className="bg-primary text-white font-manrope-semibold text-sm px-4 py-2 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-40"
                          >
                            Use this time
                          </button>
                          <button
                            onClick={() => { setCustomTimeEnabled(false); setCustomTime(""); }}
                            className="text-xs font-manrope-medium text-secondary hover:text-black transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                        {selectedSlot?.id === "custom" && (
                          <p className="font-manrope-medium text-xs text-primary flex items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                            </svg>
                            Custom time selected: {selectedSlot.startTime}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Custom Booking CTA */}
            <div className="mt-6 pt-6 border-t border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-gray-100" />
                <span className="font-manrope-regular text-xs text-gray-400 whitespace-nowrap">or need a custom arrangement?</span>
                <div className="flex-1 h-px bg-gray-100" />
              </div>
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 flex flex-col gap-3">
                <div>
                  <p className="font-manrope-semibold text-sm text-black">Custom Booking Request</p>
                  <p className="font-manrope-regular text-xs text-secondary mt-0.5">
                    Pick your own date and time. We'll review your request and send you a price offer to negotiate.
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (!user) { openModal("sign-in"); return; }
                    setShowCustomModal(true);
                  }}
                  className="flex items-center justify-center gap-2 bg-primary text-white font-manrope-semibold text-sm px-5 py-2.5 rounded-xl hover:opacity-90 transition-opacity w-full"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 9v7.5" />
                  </svg>
                  Request Custom Booking
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT — Order details + customer info */}
          <div className="w-full lg:w-80 lg:flex-shrink-0 flex flex-col gap-4">

            {/* Order summary */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm flex flex-col gap-5">
              <h2 className="font-manrope-bold text-lg text-black">Order details</h2>

              <div className="flex flex-col gap-2">
                <span className="font-manrope-medium text-sm text-secondary">Service</span>
                <div className="flex items-center gap-3">
                  <img src={image} alt={service.title} className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                  <div>
                    <p className="font-manrope-medium text-sm text-black">{service.title}</p>
                    <p className="font-manrope-bold text-base text-black">${service.price}</p>
                  </div>
                </div>
              </div>

              {selectedDate && (
                <div className="flex flex-col gap-1">
                  <span className="font-manrope-medium text-sm text-secondary">Selected date</span>
                  <span className="font-manrope-semibold text-sm text-black">
                    {new Date(selectedDate + "T00:00:00").toLocaleDateString("en-US", {
                      weekday: "long", year: "numeric", month: "long", day: "numeric",
                    })}
                  </span>
                </div>
              )}

              {selectedSlot && (
                <div className="flex flex-col gap-1">
                  <span className="font-manrope-medium text-sm text-secondary">Selected time</span>
                  <div className="flex items-center gap-1.5">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-primary">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-manrope-semibold text-sm text-black">
                      {selectedSlot.startTime} – {selectedSlot.endTime}
                    </span>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <span className="font-manrope-medium text-sm text-secondary">
                  Number of people{selectedSlot ? ` (max ${maxQty})` : ""}
                </span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="w-8 h-8 rounded-full bg-primary text-white font-manrope-bold text-lg flex items-center justify-center hover:opacity-90 transition-opacity"
                  >−</button>
                  <span className="font-manrope-semibold text-base text-black w-4 text-center">{quantity}</span>
                  <button
                    onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                    disabled={quantity >= maxQty}
                    className="w-8 h-8 rounded-full bg-primary text-white font-manrope-bold text-lg flex items-center justify-center hover:opacity-90 transition-opacity disabled:opacity-40"
                  >+</button>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-3 flex items-center justify-between">
                <span className="font-manrope-medium text-sm text-secondary">Total</span>
                <span className="font-manrope-bold text-lg text-black">${subtotal.toFixed(2)}</span>
              </div>
            </div>

            {/* Customer info */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm flex flex-col gap-4">
              <h2 className="font-manrope-bold text-base text-black">Your information</h2>

              {/* Name */}
              <div className="flex flex-col gap-1">
                <label className="font-manrope-medium text-xs text-secondary">Full name <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  placeholder={user?.name || "Enter your full name"}
                  value={form.fullName}
                  onChange={setField("fullName")}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 outline-none font-manrope-regular text-sm text-gray-700 placeholder:text-gray-400 focus:border-primary transition-colors"
                />
              </div>

              {/* Email */}
              <div className="flex flex-col gap-1">
                <label className="font-manrope-medium text-xs text-secondary">Email address <span className="text-red-400">*</span></label>
                <input
                  type="email"
                  placeholder={user?.email || "Enter your email"}
                  value={form.email}
                  onChange={setField("email")}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 outline-none font-manrope-regular text-sm text-gray-700 placeholder:text-gray-400 focus:border-primary transition-colors"
                />
              </div>

              {/* Phone */}
              <div className="flex flex-col gap-1">
                <label className="font-manrope-medium text-xs text-secondary">Phone number <span className="text-red-400">*</span></label>
                <input
                  type="tel"
                  placeholder={user?.phone || "Enter your phone number"}
                  value={form.phone}
                  onChange={setField("phone")}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 outline-none font-manrope-regular text-sm text-gray-700 placeholder:text-gray-400 focus:border-primary transition-colors"
                />
              </div>

              {/* Address */}
              <div className="flex flex-col gap-1">
                <label className="font-manrope-medium text-xs text-secondary">Address <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  placeholder="Enter your address"
                  value={form.address}
                  onChange={setField("address")}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 outline-none font-manrope-regular text-sm text-gray-700 placeholder:text-gray-400 focus:border-primary transition-colors"
                />
              </div>

              {/* Notes */}
              <div className="flex flex-col gap-1">
                <label className="font-manrope-medium text-xs text-secondary">Notes <span className="text-gray-400">(optional)</span></label>
                <textarea
                  placeholder="Any special requests or notes…"
                  value={form.notes}
                  onChange={setField("notes")}
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 outline-none font-manrope-regular text-sm text-gray-700 placeholder:text-gray-400 focus:border-primary transition-colors resize-none"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  <p className="font-manrope-medium text-sm text-red-600">{error}</p>
                </div>
              )}

              <button
                onClick={handleRequestBooking}
                disabled={isSubmitting || !selectedDate || !selectedSlot}
                className={`w-full flex items-center justify-center gap-2 font-manrope-semibold text-base px-5 py-3 rounded-[12px] transition-opacity ${
                  !selectedDate || !selectedSlot
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-primary text-white hover:opacity-90"
                } disabled:opacity-60`}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting…
                  </>
                ) : !user ? (
                  "Sign in to request booking"
                ) : (
                  <>
                    Request booking
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
